import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { requestService, certificateService } from '../services/supabaseClient';
import { certificatePdf, downloadBlob } from '../utils/certificate';
import '../styles/PartnerRequests.css';
import '../styles/Requests.css';

// ============================================================
// DB CONNECTED (restyled)
// ------------------------------------------------------------
// Same data/logic as before (requestService.getAll() + certificateService
// joined by student_id::course_id), just re-themed to match
// PartnerRequests.jsx — same `pill`, `table-wrapper`, `action-btn` classes
// from styles/PartnerRequests.css, plus a small Requests.css for the
// Needs Action / Issued / All tab strip seen on the partner-facing
// credentials view.
//
// requests.status only allows: 'registered' | 'pending' | 'completed'
// | 'cancelled' (DB check constraint). 'registered' rows never reach
// this page (filtered out on load — partner hasn't requested a
// certificate for them yet).
//
// Actual certificate PDF download now lives on the Certificates /
// PartnerCertificates pages (utils/certificate.js) — this page only
// approves/rejects and (re)issues the certificate row + PDF in storage.
// ============================================================

function Pill({ value }) {
  if (!value) return <span className="pill pill-registered">—</span>;
  const cls = value.toLowerCase();
  return <span className={`pill pill-${cls}`}>{value.replace('_', ' ')}</span>;
}

function formatDate(value) {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
}

export default function Requests() {
  const navigate = useNavigate();

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);
  const [tab, setTab] = useState('needs_action'); // 'needs_action' | 'issued' | 'all'

  /* ============================================================
     LOAD
  ============================================================ */

  const load = async () => {
    try {
      const [requests, certificates] = await Promise.all([
        requestService.getAll(),
        certificateService.getAll()
      ]);

      // certificates are per (student_id, course_id) pair, not per
      // enrollment_id, so look them up that way.
      const certificateByKey = Object.fromEntries(
        (certificates || []).map(cert => [`${cert.student_id}::${cert.course_id}`, cert])
      );

      // Only rows a partner has actually requested certification for
      // belong here — plain 'registered' rows (nobody clicked 🎓 yet)
      // stay on the partner's My Students page and never reach admin.
      const joined = (requests || [])
        .filter(request => request.status !== 'registered')
        .map(request => ({
          ...request,
          certificate: certificateByKey[`${request.student_id}::${request.course_id}`] || null
        }));

      setRows(joined);

      setSelectedIds(current => current.filter(id => joined.some(row => row.enrollment_id === id)));
    } catch (error) {
      console.error('Unable to load requests:', error);
    }
  };

  useEffect(() => {
    load();
  }, []);

  /* ============================================================
     LABELS
  ============================================================ */

  const partnerLabel = row =>
    row?.partners?.company_name ||
    [row?.partners?.first_name, row?.partners?.last_name].filter(Boolean).join(' ') ||
    '—';

  const personName = row => `${row.students?.first_name || ''} ${row.students?.last_name || ''}`.trim();

  const isCertification = row => row.course?.type === 'certification';

  /* ============================================================
     CERTIFICATE ISSUE
  ============================================================ */

  // Requests.jsx ke andar issueCertificate function ko isse replace karein:
const issueCertificate = async (row) => {
  try {
    const certificateUuid = crypto.randomUUID();
    const verifyUrl = `${window.location.origin}/verify/${certificateUuid}`;

    // 1. PDF Generate karein
    const blob = await certificatePdf({
      personName: personName(row),
      contentName: row.course?.name || '',
      certificateId: certificateUuid, // Yahan unique ID pass karein
      uuid: certificateUuid,
      verifyUrl
    });

    // 2. Storage mein upload karein
    const path = `${certificateUuid}.pdf`;
    const fileUrl = await certificateService.uploadPdf(blob, path);

    // 3. Database mein entry karein (Sahi foreign keys ke saath)
    await certificateService.add({
      student_id: row.student_id,
      course_id: row.course_id,
      partner_id: row.partner_id,
      certificate_type: row.course?.type || null,
      pdf_url: fileUrl,
      issue_date: new Date().toISOString()
    });

    return true;
  } catch (err) {
    console.error("Issue Certificate Error:", err);
    throw new Error("Failed to generate or save certificate.");
  }
};

  /* ============================================================
     SINGLE REVIEW
  ============================================================ */

  const review = async (row, status) => {
    setLoading(true);
    try {
      await requestService.updateStatus(row.enrollment_id, status);

      if (status === 'completed' && isCertification(row)) {
        await issueCertificate(row);
      }

      setSelectedIds(current => current.filter(id => id !== row.enrollment_id));
      await load();
    } catch (error) {
      alert(error?.message || 'Unable to update request.');
    } finally {
      setLoading(false);
    }
  };

  /* ============================================================
     SELECTION
  ============================================================ */

  const toggleSelection = id => {
    setSelectedIds(current =>
      current.includes(id) ? current.filter(item => item !== id) : [...current, id]
    );
  };

  const pendingRows = rows.filter(row => row.status === 'pending');

  const allPendingSelected =
    pendingRows.length > 0 && pendingRows.every(row => selectedIds.includes(row.enrollment_id));

  const toggleSelectAll = () => {
    setSelectedIds(allPendingSelected ? [] : pendingRows.map(row => row.enrollment_id));
  };

  /* ============================================================
     BULK ACTIONS
  ============================================================ */

  const approveSelected = async () => {
    const selectedRows = rows.filter(
      row => selectedIds.includes(row.enrollment_id) && row.status === 'pending'
    );
    if (!selectedRows.length) return;
    if (!window.confirm(`Approve ${selectedRows.length} selected request(s)?`)) return;

    setLoading(true);
    try {
      for (const row of selectedRows) {
        await requestService.updateStatus(row.enrollment_id, 'completed');
        if (isCertification(row)) await issueCertificate(row);
      }
      setSelectedIds([]);
      await load();
    } catch (error) {
      alert(error?.message || 'Some requests could not be approved.');
      await load();
    } finally {
      setLoading(false);
    }
  };

  const rejectSelected = async () => {
    const selectedRows = rows.filter(
      row => selectedIds.includes(row.enrollment_id) && row.status === 'pending'
    );
    if (!selectedRows.length) return;
    if (!window.confirm(`Reject ${selectedRows.length} selected request(s)?`)) return;

    setLoading(true);
    try {
      for (const row of selectedRows) {
        await requestService.updateStatus(row.enrollment_id, 'cancelled');
      }
      setSelectedIds([]);
      await load();
    } catch (error) {
      alert(error?.message || 'Some requests could not be rejected.');
      await load();
    } finally {
      setLoading(false);
    }
  };

  const retryIssueCertificate = async row => {
    setLoading(true);
    try {
      await issueCertificate(row);
      await load();
    } catch (error) {
      alert(error?.message || 'Unable to issue certificate.');
    } finally {
      setLoading(false);
    }
  };

  /* ============================================================
     TABS
  ============================================================ */

  const needsActionRows = rows.filter(row => row.status === 'pending');
  const issuedRows = rows.filter(row => row.status === 'completed');

  const visibleRows = tab === 'needs_action' ? needsActionRows : tab === 'issued' ? issuedRows : rows;

  /* ============================================================
     UI
  ============================================================ */

  return (
    <div className="panel">
      <div className="panel-head">
        <div>
          <span className="eyebrow">Requests</span>
          <h2>Certification Requests</h2>
          <p>Review certification requests raised by partners.</p>
        </div>
      </div>

      <div className="req-tabs">
        <button
          type="button"
          className={`req-tab ${tab === 'needs_action' ? 'active' : ''}`}
          onClick={() => setTab('needs_action')}
        >
          Needs action <span className="req-tab-count">{needsActionRows.length}</span>
        </button>
        <button
          type="button"
          className={`req-tab ${tab === 'issued' ? 'active' : ''}`}
          onClick={() => setTab('issued')}
        >
          Issued <span className="req-tab-count">{issuedRows.length}</span>
        </button>
        <button
          type="button"
          className={`req-tab ${tab === 'all' ? 'active' : ''}`}
          onClick={() => setTab('all')}
        >
          All <span className="req-tab-count">{rows.length}</span>
        </button>
      </div>

      {selectedIds.length > 0 && (
        <div className="bulk-action-bar" style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '16px' }}>
          <strong>{selectedIds.length} selected</strong>
          <button type="button" className="action-btn" disabled={loading} onClick={approveSelected}>
            Approve Selected
          </button>
          <button type="button" className="action-btn" disabled={loading} onClick={rejectSelected}>
            Reject Selected
          </button>
        </div>
      )}

      <div className="table-wrapper">
        <table className="requests-table">
          <thead>
            <tr>
              <th>
                <input
                  type="checkbox"
                  checked={allPendingSelected}
                  onChange={toggleSelectAll}
                  disabled={!pendingRows.length || loading || tab === 'issued'}
                />
              </th>
              <th>Enrolment</th>
              <th>Person</th>
              <th>Content</th>
              <th>Kind</th>
              <th>Partner</th>
              <th>Payment</th>
              <th>State</th>
              <th>Created</th>
              <th>Action</th>
            </tr>
          </thead>

          <tbody>
            {!visibleRows.length && (
              <tr>
                <td colSpan={10} className="empty-state">
                  {tab === 'needs_action' ? 'Nothing needs action right now.' : 'No requests yet.'}
                </td>
              </tr>
            )}

            {visibleRows.map(row => {
              const needsCertificateRetry =
                row.status === 'completed' && isCertification(row) && !row.certificate;
              const isSelected = selectedIds.includes(row.enrollment_id);

              return (
                <tr key={row.enrollment_id}>
                  <td>
                    {row.status === 'pending' && (
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => toggleSelection(row.enrollment_id)}
                        disabled={loading}
                      />
                    )}
                  </td>

                  <td className="enrolment-id-cell">{row.enrollment_id}</td>

                  <td className="person-cell">
                    <b>{personName(row)}</b>
                    <small>{row.students?.email}</small>
                  </td>

                  <td>
                    {row.course?.name || '—'}
                    {row.course?.course_code ? ` (${row.course.course_code})` : ''}
                  </td>

                  <td>
                    <Pill value={row.course?.type} />
                  </td>

                  <td>{partnerLabel(row)}</td>

                  <td>
                    {isCertification(row) ? (
                      <>
                        <Pill value={row.payment_status} />
                        {row.course?.price ? ` · ₹${row.course.price}` : ''}
                      </>
                    ) : (
                      'Not required'
                    )}
                  </td>

                  <td>
                    <Pill value={row.status} />
                  </td>

                  <td>{formatDate(row.created_at)}</td>

                  <td className="action-cell">
                    {row.status === 'pending' && (
                      <>
                        <button className="action-btn" disabled={loading} onClick={() => review(row, 'completed')} type="button">
                          Approve
                        </button>
                        <button className="action-btn danger" disabled={loading} onClick={() => review(row, 'cancelled')} type="button">
                          Reject
                        </button>
                      </>
                    )}

                    {needsCertificateRetry && (
                      <button className="action-btn" disabled={loading} onClick={() => retryIssueCertificate(row)} type="button">
                        Issue Certificate
                      </button>
                    )}

                    {row.status === 'completed' && row.certificate && (
                      <button
                        className="action-btn"
                        type="button"
                        onClick={() => navigate('/academy/admin/certificates', { state: { enrollmentId: row.enrollment_id } })}
                      >
                        View Certificate
                      </button>
                    )}

                    {row.status === 'cancelled' && <span className="action-btn" style={{ opacity: 0.5 }}>—</span>}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}